import React, { Fragment, useContext, useEffect, useReducer, useRef } from 'react';
import { LocaleContext, NotificationsContext, NotificationTypes } from 'shared';
import { convertFileToBase64 } from 'utils';

import { ADD_FILE, REMOVE_FILE, uploadFilesReducer } from './reducer/uploadFilesReducer';
import { UploadFieldProps } from './UploadField.types';
import { UploadFieldInput } from './uploadFieldInput/UploadFieldInput';

export const UploadField = ({
  input,
  onFileNameChange,
  className,
  meta,
  multiple,
  allowedExtensions,
  maxFilesize,
  label,
  maxCount = 1,
}: UploadFieldProps) => {
  const [state, dispatch] = useReducer(uploadFilesReducer, { files: [] });
  const ref = useRef<HTMLInputElement>(null);
  const localeContext = useContext(LocaleContext);
  const notificationsContext = useContext(NotificationsContext);

  useEffect(() => {
    const value = state.files.map(f => f.base64);
    const name = state.files.map(f => f.name);

    if (value !== input.value) {
      input.onChange(value);
      if (name && name.length > 0 && onFileNameChange) {
        onFileNameChange(name[0]);
      }
    }
  }, [state, input, onFileNameChange]);

  const handleRemoveFile = (index: number) => () => {
    dispatch({ type: REMOVE_FILE, index });

    if (ref.current) {
      ref.current.value = '';
    }
  };

  const handleSelectFiles = () => {
    if (ref.current) {
      ref.current.click();
    }
  };

  const handleChange = () => {
    if (ref.current && ref.current.files) {
      if (input.value.length + ref.current.files.length > maxCount) {
        notificationsContext.showNotification({
          message: localeContext.formatMessage({ id: 'FORM.MAX-FILE-COUNT-EXCEEDED' }),
          type: NotificationTypes.ERROR,
        });

        return;
      }

      Array.from(ref.current.files).forEach(file => {
        if (file.size > maxFilesize) {
          notificationsContext.showNotification({
            message: localeContext.formatMessage({ id: 'FORM.MAX-FILE-SIZE-EXCEEDED' }),
            type: NotificationTypes.ERROR,
          });

          return;
        }

        convertFileToBase64(file).then(base64 => dispatch({ type: ADD_FILE, file: { name: file.name, base64 } }));
      });
    }
  };

  return (
    <Fragment>
      <UploadFieldInput
        className={className}
        disabled={maxCount === state.files.length}
        files={state.files}
        onRemove={handleRemoveFile}
        onSelectFiles={handleSelectFiles}
        label={label}
        meta={meta}
      >
        <input
          type="file"
          onChange={handleChange}
          multiple={multiple}
          ref={ref}
          accept={allowedExtensions.join(', ')}
          className="input_file"
        />
      </UploadFieldInput>
    </Fragment>
  );
};
